import React from 'react';
import TextInput from '../textInput/TextInput';
import Button from '../button/Button';

export type TextButtonInputProps = {
    /** An error message to display */
    error?: string;
    /** Additional styles to wrap */
    className?: string;
    /** The name reference */
    name: string;
    /** The placeholder value for the input */
    placeHolder?: string;
    /** The label that appears above the input */
    label?: string;
    /** The current value */
    value?: string;
    /** An index of what piece of data is being modified */
    dataKey?: string;
    /** Decides if the input is editable */
    readOnly?: boolean;
    /** Decides if the input is focused automatically on page load */
    autoFocus?: boolean;
    /** Allows enter to be pressed to blur out of the input (will not trigger the button)*/
    handleReturn?: boolean;
    /** Required for the elements to link up together in BS5 forms */
    elementLinkId: string;
    /** Stops propagation on the event that is trigged*/
    stopPropagationOnClick?: boolean;
    /**If a floating label is used */
    floatingLabel?: boolean;
    /**The maximum length of the input component */
    maxLength?: number;
    /** A function to specify what should happen when the input is focused */
    onFocus?: (event: React.FormEvent<HTMLInputElement>) => void;
    /** A function that can be trigged when a key is pressed, can also handle bluring on enter if handleReturn is true */
    onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    /** A function to be called on every key stroke to pass the value back up to it's controller */
    onChange?: (event: React.FormEvent<HTMLInputElement>) => void;
    /** A function to be called when bluring out of the input */
    onLeave?: (event: React.FormEvent<HTMLInputElement>) => void;
    /** Passed down to Button component - If the button is disabled and can't be clicked */
    buttonDisabled?: boolean;
    /** Passed down to Button component - A function that registers what the click of the button does */
    onButtonClick: () => void;
    /** Passed down to Button component - If the button is currently in waiting mode or not */
    waitValue?: boolean;
    /** Passed down to Button component - Decides if the button should have a timer when clicked*/
    waitOnClick?: boolean;
    /** Passed down to Button component - Any extra styling required for the button*/
    buttonStyle?: string;
    /** Passed down to Button component - The text on the button */
    buttonText?: string;
    /** Passed down to Button component - The text to be displayed when the button is waiting after being clicked */
    waitText?: string;
};

/** Responsible for having an input field with a button styled nicely next to it. This can be used for fields where input is required along with a search afterwards. This component uses the "Button" component and requires it's props to be passed down to the button*/
const TextButtonInput = (props: TextButtonInputProps): JSX.Element => {
    const {
        error,
        className,
        name,
        placeHolder,
        label,
        value,
        dataKey = '',
        maxLength = 10000,
        readOnly,
        autoFocus,
        elementLinkId,
        handleReturn,
        stopPropagationOnClick,
        floatingLabel = false,
        onFocus,
        onKeyPress,
        onChange,
        onLeave,
        onButtonClick,
        waitValue = false,
        waitOnClick = false,
        buttonStyle,
        buttonText = '',
        waitText
    } = props;

    const onChangeInput = (event: React.FormEvent<HTMLInputElement>) => {
        onChange && onChange(event);
    };

    return (
        <>
            <TextInput
                ariaDescribeLink={elementLinkId}
                name={`${name}-textInput`}
                className={className}
                error={error}
                placeholder={placeHolder}
                label={label}
                value={value}
                dataKey={dataKey}
                maxLength={maxLength}
                floatingLabel={floatingLabel}
                readOnly={readOnly}
                autoFocus={autoFocus}
                handleReturn={handleReturn}
                stopPropagationOnClick={stopPropagationOnClick}
                onChange={onChangeInput}
                onLeave={onLeave}
                onFocus={onFocus}
                onKeyPress={onKeyPress}>
                <Button
                    id={elementLinkId}
                    name={`${name}-button`}
                    className={`btn btn-primary base-level-index ${buttonStyle}`}
                    onClick={onButtonClick}
                    text={buttonText}
                    waitText={waitText}
                    waitValue={waitValue}
                    waitOnClick={waitOnClick}
                />
            </TextInput>
        </>
    );
};

export default TextButtonInput;
